import axios, { type AxiosError } from "axios";
import { CONFIG } from "@/config";
import { getHeaders } from "@/api/getHeaders";
import { getLogoutCallback } from "@/auth/logoutHandler";
import { snackbarUtils } from "@/utils";

const api = axios.create({
  baseURL: CONFIG.API_URL,
});

api.interceptors.request.use(
  async (config) => {
    try {
      const headers = await getHeaders();
      if (headers) {
        config.headers = { ...config.headers, ...headers } as typeof config.headers;
      }
    } catch (error) {
      console.error("Error getting headers:", error);
    }
    return config;
  },
  (error: AxiosError) => Promise.reject(error),
);

interface ApiErrorPayload {
  detail?: string;
  message?: string;
}

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiErrorPayload>) => {
    const status = error.response?.status;
    const detail =
      error.response?.data?.detail ?? error.response?.data?.message ?? "";

    if (status === 403 && String(detail).includes("Admin access required")) {
      snackbarUtils.showError(detail);
      const logout = getLogoutCallback();
      if (typeof logout === "function") {
        logout({ logoutParams: { returnTo: window.location.origin } });
      }
    } else {
      // Log status/response body/message only - the full AxiosError also
      // carries the live Authorization header via error.config.headers.
      console.error("API Error:", status, error.response?.data ?? error.message);
    }
    return Promise.reject(error);
  },
);

export { api };
